import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ShieldCheck, Calendar, AlertTriangle, History, Loader2, RefreshCw } from "lucide-react";
import FOHGuidancePanel from "./FOHGuidancePanel";
import SmartDateInput from "../SmartDateInput";
import { API } from "@/contexts/AuthContext";

/**
 * MOT Companion — skeleton screen for the MOT-only path of the Yestimate
 * Intake. Section headers + sample field structure so Phase 2 can wire the
 * actual capture/validation logic without re-architecting the layout.
 *
 *   Props
 *     data      — { mot_due_date?, last_advisories?, customer_aware_of_advisories? }
 *     onChange  — (patch) => void
 */
export default function MOTCompanion({ data = {}, onChange = () => {}, vehicleReg = "" }) {
  const set = (key) => (e) => onChange({ [key]: e?.target ? e.target.value : e });

  // ------------------------------------------------------------------------
  // DVSA last-advisories auto-populate
  //
  // When MOTCompanion mounts and we have a registration, hit the lightweight
  // /intake/mot-last-advisories/{reg} endpoint and seed the textarea with
  // the advisory-class items (ADVISORY / MINOR / PASS_ADVISORY) from the
  // customer's most recent MOT. The FOH can then annotate / strike-through
  // anything the customer's already actioned — keeps the conversation
  // flowing instead of asking them to recall from memory.
  //
  // Only fires when:
  //   • we have a non-empty reg
  //   • the textarea is empty (don't clobber FOH edits)
  //   • we haven't already attempted a fetch for THIS reg
  //
  // Important: the parent re-creates `onChange` and mutates `data` on every
  // keystroke. We CANNOT include those in the effect deps or each parent
  // re-render would cancel the in-flight DVSA fetch and the loading spinner
  // would get stuck. We track attempt-per-reg in a ref so the gate survives
  // parent re-renders without re-running the effect.
  // ------------------------------------------------------------------------
  const [lookup, setLookup] = useState({
    loading: false,
    attempted: false,
    found: false,
    reason: null,
    lastTestDate: null,
  });
  const attemptedRegRef = useRef(null);
  // Always-fresh refs so the effect can read the latest values without
  // re-subscribing (and re-cancelling) on every parent render.
  const onChangeRef = useRef(onChange);
  const lastAdvisoriesRef = useRef(data.last_advisories);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { lastAdvisoriesRef.current = data.last_advisories; }, [data.last_advisories]);

  useEffect(() => {
    const reg = (vehicleReg || "").trim();
    if (!reg) return;
    if (attemptedRegRef.current === reg) return;
    if ((lastAdvisoriesRef.current || "").trim()) {
      // Field already has content — don't overwrite.
      attemptedRegRef.current = reg;
      setLookup((p) => ({ ...p, attempted: true }));
      return;
    }
    attemptedRegRef.current = reg;
    setLookup({
      loading: true,
      attempted: true,
      found: false,
      reason: null,
      lastTestDate: null,
    });
    // Note: deliberately NO `let cancelled` flag here. React 18 StrictMode
    // synthetically unmounts/remounts the component on first paint, which
    // would fire any cleanup and flip `cancelled = true`, stranding the
    // loading spinner. Instead we guard the response handler against a
    // stale reg via attemptedRegRef — only the response for the reg that
    // is *currently* registered wins. `setLookup` on a genuinely unmounted
    // component is a no-op in React 18, so we don't need an explicit
    // cancellation.
    (async () => {
      try {
        const r = await axios.get(
          `${API}/intake/mot-last-advisories/${encodeURIComponent(reg)}`
        );
        if (attemptedRegRef.current !== reg) return; // reg changed mid-flight
        if (r.data?.found && r.data.advisories_text) {
          onChangeRef.current({ last_advisories: r.data.advisories_text });
        }
        setLookup({
          loading: false,
          attempted: true,
          found: !!r.data?.found,
          reason: r.data?.reason || null,
          lastTestDate: r.data?.last_test_date || null,
        });
      } catch {
        if (attemptedRegRef.current === reg) {
          // Silent — FOH can type the advisories themselves.
          setLookup((p) => ({ ...p, loading: false, attempted: true }));
        }
      }
    })();
  }, [vehicleReg]);

  // Manual re-fetch (in case the FOH wants to reset back to the DVSA pull
  // after typing over it).
  const handleRefetch = async () => {
    const reg = (vehicleReg || "").trim();
    if (!reg) return;
    setLookup((p) => ({ ...p, loading: true }));
    try {
      const r = await axios.get(
        `${API}/intake/mot-last-advisories/${encodeURIComponent(reg)}`
      );
      if (r.data?.found) {
        onChange({ last_advisories: r.data.advisories_text || "" });
      }
      setLookup({
        loading: false,
        attempted: true,
        found: !!r.data?.found,
        reason: r.data?.reason || null,
        lastTestDate: r.data?.last_test_date || null,
      });
    } catch {
      setLookup((p) => ({ ...p, loading: false }));
    }
  };

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4"
      data-testid="mot-companion"
    >
      {/* Center — capture columns */}
      <div className="space-y-3">
        <CompanionHeader
          tone="blue"
          icon={<ShieldCheck className="w-4 h-4" />}
          eyebrow="Reason · MOT"
          title="MOT booking"
          subtitle="Confirm timing + flag any advisories the customer is already aware of."
        />

        <Section
          icon={<Calendar className="w-3.5 h-3.5" />}
          label="Timing"
          testId="mot-companion-timing"
        >
          <FieldRow label="MOT due date">
            <SmartDateInput
              value={data.mot_due_date || ""}
              onChange={set("mot_due_date")}
              dataTestid="mot-due-date"
              hint
            />
            <Hint>Auto-prefilled from DVLA when the reg is recognised.</Hint>
          </FieldRow>
          <FieldRow label="Preferred slot time">
            {/* iter199aj (Feb 2026) — Replaced the legacy window
                dropdown (Morning / Afternoon / End-of-day / Flexible)
                with a precise HH:MM stepper. Single source of truth for
                MOT bookings — FOH books a specific tester slot, not a
                vague window. Stored on the same field so legacy data
                still loads (the dropdown values are ignored as long
                as the new picker overwrites them). */}
            <input
              type="time"
              step="300"
              value={data.preferred_slot_window || ""}
              onChange={set("preferred_slot_window")}
              className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 focus:outline-none focus:border-teal-500 font-mono tracking-wider"
              data-testid="mot-preferred-slot"
              aria-label="MOT slot time"
            />
            <Hint>HH:MM in 5-minute increments. Leave blank if no slot booked yet.</Hint>
          </FieldRow>
        </Section>

        <Section
          icon={<History className="w-3.5 h-3.5" />}
          label="History context"
          testId="mot-companion-history"
        >
          <FieldRow label="Last MOT advisories">
            <div className="relative">
              <textarea
                rows={3}
                placeholder={
                  lookup.loading
                    ? "Loading from DVSA…"
                    : lookup.attempted && !lookup.found
                    ? "No DVSA advisories found" +
                      (lookup.reason === "no_record"
                        ? " (vehicle not on DVSA register)"
                        : lookup.reason === "no_tests_recorded"
                        ? " (no MOT history yet)"
                        : "") +
                      " — type any the customer recalls."
                    : "Auto-loaded from DVSA — paste / annotate anything the customer mentions."
                }
                value={data.last_advisories || ""}
                onChange={set("last_advisories")}
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-teal-500"
                data-testid="mot-last-advisories"
              />
              {lookup.loading && (
                <span
                  className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11.5px] text-slate-500 bg-white/80 px-1.5 py-0.5 rounded"
                  data-testid="mot-advisories-loading"
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  DVSA
                </span>
              )}
            </div>
            {lookup.attempted && !lookup.loading && (
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <span className="text-[11.5px] text-slate-500">
                  {lookup.found
                    ? "Auto-loaded from DVSA" +
                      (lookup.lastTestDate
                        ? " · last test " +
                          formatLastTestDate(lookup.lastTestDate)
                        : "")
                    : null}
                </span>
                {vehicleReg && (
                  <button
                    type="button"
                    onClick={handleRefetch}
                    className="inline-flex items-center gap-1 text-[11.5px] text-teal-700 hover:text-teal-900 font-semibold"
                    data-testid="mot-advisories-refetch"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Re-fetch
                  </button>
                )}
              </div>
            )}
          </FieldRow>
          <FieldRow label="Customer aware of advisories?">
            <RadioInline
              name="customer_aware"
              value={data.customer_aware_of_advisories}
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
                { value: "unsure", label: "Not sure" },
              ]}
              onChange={(v) => onChange({ customer_aware_of_advisories: v })}
              testId="mot-customer-aware"
            />
          </FieldRow>
        </Section>

        <Section
          icon={<AlertTriangle className="w-3.5 h-3.5" />}
          label="Pre-test concerns"
          testId="mot-companion-concerns"
        >
          <FieldRow label="Anything noticed since last MOT?">
            <textarea
              rows={2}
              placeholder="Symptoms / new noises / dash lights — keep it short, the technician will follow up."
              value={data.pre_test_concerns || ""}
              onChange={set("pre_test_concerns")}
              className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-teal-500"
              data-testid="mot-pre-test-concerns"
            />
          </FieldRow>
        </Section>
      </div>

      {/* Right rail — FOH Guidance */}
      <FOHGuidancePanel
        tone="teal"
        title="MOT guidance"
        askNext={[
          "When does the current MOT expire — exact date if they have it.",
          "Are they aware of advisories from last year? Pull them up and walk through.",
        ]}
        sayToCustomer={[
          "We'll send the MOT certificate by email and text the same day.",
          "Advisories aren't failures — but flagging them now means the technician can review properly.",
        ]}
        watchOuts={[
          "If MOT is expired, the vehicle cannot be driven to us — offer collection.",
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function CompanionHeader({ tone, icon, eyebrow, title, subtitle }) {
  const bg = TONE_HEADER[tone] || TONE_HEADER.blue;
  return (
    <div
      className={`relative overflow-hidden rounded-lg px-4 py-3 text-white ${bg}`}
    >
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_55%)]" />
      <div className="relative flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-md bg-white/20 border border-white/30 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold opacity-80">
            {eyebrow}
          </p>
          <h3 className="text-[17px] font-bold leading-tight truncate">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[12.5px] opacity-90 leading-snug mt-0.5 max-w-[60ch]">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, label, testId, children }) {
  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid={testId}
    >
      <header className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-slate-100">
        <span className="text-slate-500">{icon}</span>
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-700 font-semibold">
          {label}
        </p>
      </header>
      <div className="px-4 py-3.5 space-y-3">{children}</div>
    </section>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] font-semibold text-slate-700 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Hint({ children }) {
  return <p className="text-[12px] text-slate-500 mt-1">{children}</p>;
}

function RadioInline({ name, value, options, onChange, testId }) {
  return (
    <div className="flex gap-2 flex-wrap" data-testid={testId}>
      {options.map((opt) => {
        const isOn = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 rounded-full border text-[13.5px] font-medium transition-colors ${
              isOn
                ? "border-teal-500 bg-teal-50 text-teal-800"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
            data-testid={`${testId}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const TONE_HEADER = {
  blue: "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700",
  amber: "bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600",
  teal: "bg-gradient-to-r from-teal-600 via-teal-700 to-emerald-700",
};

function formatLastTestDate(raw) {
  if (!raw) return "";
  try {
    // DVSA may return "2024.05.12" or full ISO — normalise both.
    const cleaned = String(raw).replace(/\./g, "-").slice(0, 10);
    const d = new Date(cleaned);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
}
